import { useCallback, useMemo } from 'react';
import { Box, CircularProgress, InputAdornment, TextField } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';

import { Chip } from '@occc/ui-design-system';
import { CALENDAR_GRID_DEFAULTS } from '@occc/ui-design-system';

type KioskSelectorInputProps = {
  selectedKioskIds: string[];
  onRemoveKiosk: (kioskId: string) => void;
  onClick: () => void;
  placeholder?: string;
  loading?: boolean;
};

export const KioskSelectorInput = ({
  selectedKioskIds,
  onRemoveKiosk,
  onClick,
  placeholder = 'Terminal/Kiosk IDs',
  loading = false,
}: KioskSelectorInputProps) => {
  const handleRemoveKiosk = useCallback(
    (kioskId: string) => (event: React.MouseEvent) => {
      event.stopPropagation();
      onRemoveKiosk(kioskId);
    },
    [onRemoveKiosk],
  );

  const hasSelectedKiosks = selectedKioskIds.length > 0;

  const inputValue = useMemo(() => {
    if (hasSelectedKiosks) {
      return ' ';
    }
    return '';
  }, [hasSelectedKiosks]);

  return (
    <Box onClick={onClick} sx={{ cursor: 'pointer', minWidth: 280 }}>
      <TextField
        fullWidth
        placeholder={hasSelectedKiosks ? undefined : placeholder}
        value={inputValue}
        disabled={loading}
        InputProps={{
          readOnly: true,
          startAdornment: hasSelectedKiosks ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                gap: 0.5,
                flexWrap: 'wrap',
                py: 0.5,
              }}
            >
              {selectedKioskIds.map((kioskId, index) => {
                const colorsCount = CALENDAR_GRID_DEFAULTS.dayItemColors.length;
                const colorIndex = index % colorsCount;
                const bgColor = CALENDAR_GRID_DEFAULTS.dayItemColors[colorIndex];
                const textColor = CALENDAR_GRID_DEFAULTS.dayItemTextColors[colorIndex];

                return (
                  <Chip
                    key={kioskId}
                    label={kioskId}
                    size='small'
                    onDelete={handleRemoveKiosk(kioskId)}
                    sx={{
                      borderWidth: 1,
                      borderColor: textColor,
                      backgroundColor: bgColor,
                      color: textColor,
                      '& .MuiChip-deleteIcon': {
                        color: textColor,
                        '&:hover': {
                          color: bgColor,
                          opacity: 0.7,
                        },
                      },
                    }}
                  />
                );
              })}
            </Box>
          ) : undefined,
          endAdornment: (
            <InputAdornment position='end'>
              {loading ? <CircularProgress size={20} /> : <SearchIcon color='action' />}
            </InputAdornment>
          ),
          sx: {
            cursor: 'pointer',
            justifyContent: 'space-between',
            '& input': {
              cursor: 'pointer',
              width: hasSelectedKiosks ? 0 : '100%',
            },
          },
        }}
        size='small'
      />
    </Box>
  );
};
